import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { getUserProfile, saveUserProfile } from '../utils/userPreferences';
import { useTheme } from '../context/ThemeContext';

const ProfileSettings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { darkMode } = useTheme();
  const [profile, setProfile] = useState({
    name: '',
    age: '',
    mobile: '',
    email: '',
    picture: ''
  });
  const [authUser, setAuthUser] = useState(null);

  useEffect(() => {
    // Load local profile
    const stored = getUserProfile();
    setProfile(stored);

    // Try to get authenticated user data
    const fetchAuthUser = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setAuthUser(userData);
          // Merge auth user data with local profile
          setProfile(prev => ({
            ...prev,
            name: userData.name || prev.name,
            email: userData.email || prev.email,
            mobile: userData.mobile || prev.mobile,
            age: userData.age || prev.age,
            picture: userData.picture || prev.picture
          }));
        }
      } catch (error) {
        console.log('Not authenticated or error fetching user');
      }
    };
    fetchAuthUser();
  }, []);

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Save to local storage
    const localResult = saveUserProfile(profile);
    
    // If authenticated, also save to backend
    if (authUser) {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/auth/profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            name: profile.name,
            mobile: profile.mobile,
            age: profile.age ? parseInt(profile.age) : null
          })
        });

        if (response.ok) {
          toast({
            title: 'Profile Saved',
            description: 'Your profile has been updated successfully',
          });
        } else {
          toast({
            title: 'Profile Saved Locally',
            description: 'Saved to device. Server update failed.',
          });
        }
      } catch (error) {
        toast({
          title: 'Profile Saved Locally',
          description: 'Saved to device. Could not sync with server.',
        });
      }
    } else {
      if (localResult.success) {
        toast({
          title: 'Profile Saved',
          description: 'Your profile has been updated successfully',
        });
      } else {
        toast({
          title: 'Error',
          description: localResult.message,
          variant: 'destructive'
        });
      }
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/settings')}
            className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            <ArrowLeft className={`w-6 h-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} />
          </button>
          <div>
            <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Profile & Preferences</h1>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manage your personal information</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="px-4 py-6 max-w-2xl mx-auto">
        {/* Profile Picture */}
        {profile.picture && (
          <div className="flex justify-center mb-6">
            <img
              src={profile.picture}
              alt="Profile"
              className="w-24 h-24 rounded-full object-cover border-4 border-blue-500"
            />
          </div>
        )}
        {!profile.picture && (
          <div className="flex justify-center mb-6">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <User className={`w-12 h-12 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </div>
          </div>
        )}

        {/* Auth Provider Badge */}
        {authUser && (
          <div className="flex justify-center mb-6">
            <span className={`px-3 py-1 rounded-full text-sm ${
              authUser.auth_provider === 'google' 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-green-100 text-green-700'
            }`}>
              {authUser.auth_provider === 'google' ? '🔗 Google Account' : '✉️ Email Account'}
            </span>
          </div>
        )}

        <div className={`rounded-lg shadow-sm p-6 space-y-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="space-y-2">
            <Label htmlFor="name" className={darkMode ? 'text-gray-200' : ''}>Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter your name"
              value={profile.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={`text-base ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className={darkMode ? 'text-gray-200' : ''}>
              Email ID {authUser && <span className="text-gray-500">(from account)</span>}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email address"
              value={profile.email}
              onChange={(e) => handleChange('email', e.target.value)}
              disabled={authUser?.auth_provider === 'google'}
              className={`text-base ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''} ${authUser?.auth_provider === 'google' ? 'opacity-70' : ''}`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobile" className={darkMode ? 'text-gray-200' : ''}>Mobile Number (Optional)</Label>
            <Input
              id="mobile"
              type="tel"
              placeholder="Enter your mobile number"
              value={profile.mobile}
              onChange={(e) => handleChange('mobile', e.target.value)}
              className={`text-base ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="age" className={darkMode ? 'text-gray-200' : ''}>Age (Optional)</Label>
            <Input
              id="age"
              type="number"
              placeholder="Enter your age"
              value={profile.age}
              onChange={(e) => handleChange('age', e.target.value)}
              className={`text-base ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}`}
            />
          </div>

          <div className="pt-4">
            <Button
              onClick={handleSave}
              className="w-full bg-blue-700 hover:bg-blue-800 text-white py-6 text-lg"
            >
              <Save className="w-5 h-5 mr-2" />
              Save Profile
            </Button>
          </div>
        </div>

        <p className={`text-sm text-center mt-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {authUser 
            ? 'Your profile is synced with your account.' 
            : 'All fields are optional. Your information is stored locally on your device.'}
        </p>
      </div>
    </div>
  );
};

export default ProfileSettings;